import {
  getCartQuantityStep,
  normalizePieceQuantity,
  resolveProductConditioning,
} from "@/lib/product-units";

type WishlistProduct = {
  id: string;
  status?: string;
  metadata?: Record<string, unknown> | null;
  variants?: Array<{
    id: string;
    metadata?: Record<string, unknown> | null;
  }> | null;
};

type WishlistItem = {
  productId: string;
  quantity?: number;
};

type ProductCustomization = {
  surface?: number;
};

type WishlistCartItem = {
  productId: string;
  variantId: string;
  variantIds: string[];
  quantity: number;
};

type WishlistCartPlan =
  | { kind: "ready"; items: WishlistCartItem[] }
  | { kind: "blocked"; productIds: string[] };

type BuildWishlistCartPlanInput = {
  products: WishlistProduct[];
  wishlistItems: WishlistItem[];
  quantityOverrides: Record<string, number | undefined>;
  customizations: Record<string, ProductCustomization | undefined>;
  cartVariantIds?: string[];
};

export const buildWishlistCartPlan = ({
  products,
  wishlistItems,
  quantityOverrides,
  customizations,
  cartVariantIds = [],
}: BuildWishlistCartPlanInput): WishlistCartPlan => {
  const wishlistQuantities = new Map(wishlistItems.map((item) => [item.productId, item.quantity]));
  const cartVariants = new Set(cartVariantIds);
  const blockedProductIds: string[] = [];
  const items: WishlistCartItem[] = [];

  for (const product of products) {
    if (product.status === "draft") continue;

    const variants = product.variants ?? [];
    if (variants.length === 0) {
      blockedProductIds.push(product.id);
      continue;
    }

    const selectedSurface = customizations[product.id]?.surface;
    const surfaceMin = product.metadata?.surface_min;
    const selectedVariant =
      variants.find((variant) => variant.metadata?.surface == selectedSurface) ??
      variants.find((variant) => cartVariants.has(variant.id)) ??
      variants.find((variant) => variant.metadata?.surface == surfaceMin) ??
      variants[0];

    const conditioning = resolveProductConditioning(
      product.metadata?.conditioning,
      product.metadata?.conditioning_bac
    );
    const requestedQuantity =
      quantityOverrides[product.id] ?? wishlistQuantities.get(product.id) ?? getCartQuantityStep(conditioning);

    items.push({
      productId: product.id,
      variantId: selectedVariant.id,
      variantIds: variants.map((variant) => variant.id),
      quantity: normalizePieceQuantity(requestedQuantity, conditioning),
    });
  }

  return blockedProductIds.length > 0 ? { kind: "blocked", productIds: blockedProductIds } : { kind: "ready", items };
};
